import {
  defaultOfferOption,
  formatCents,
  formatOrderDay,
  FrequencySelect,
  offerDiscountLabel,
  offerOption,
  type SubscribeAndSaveOffer,
  type SubscriptionFrequency,
} from '@brandclub/common-ui';
import { CSSProperties, useId, useState } from 'react';

import { useProductSubscribeAndSaveOffer } from '@/api/cache/subscribeAndSaveQueryOptions';
import { formatMoney } from '../../utils/string';

/** The purchase option the shopper picked on the product page. No frequency is a one-time purchase. */
export interface SubscribeAndSaveChoice {
  offer?: SubscribeAndSaveOffer;
  frequency?: SubscriptionFrequency;
  setFrequency: (frequency: SubscriptionFrequency | undefined) => void;
}

/** One-time is the default. A frequency the current variant's offer does not list becomes the offer's default one. */
export function useSubscribeAndSaveChoice(stacklineSku: string | undefined): SubscribeAndSaveChoice {
  const { data } = useProductSubscribeAndSaveOffer(stacklineSku);
  const offer = data?.options.length ? data : undefined;
  const [chosen, setFrequency] = useState<SubscriptionFrequency>();
  const frequency = offer && chosen ? (offerOption(offer, chosen) ?? defaultOfferOption(offer)).frequency : undefined;
  return { offer, frequency, setFrequency };
}

/** The selected row takes the color of the solid Add to bag button. */
const BUTTON_COLOR = 'var(--dtc-palette-primary-main, #181d56)';

const rowStyle = (selected: boolean): CSSProperties => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: selected ? '13px 15px' : '14px 16px',
  border: selected ? `2px solid ${BUTTON_COLOR}` : '1px solid rgba(0, 0, 0, 0.15)',
  borderRadius: 14,
});

const optionStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' };
const radioStyle: CSSProperties = { margin: 0, accentColor: BUTTON_COLOR };
const pillStyle: CSSProperties = {
  marginLeft: 8,
  padding: '2px 8px',
  borderRadius: 999,
  background: 'rgba(0, 0, 0, 0.06)',
  fontSize: 12,
  fontWeight: 600,
};
const mutedStyle: CSSProperties = { fontSize: 13, color: 'rgba(0, 0, 0, 0.6)' };

/** The purchase options above the in-page Add to bag button. Renders nothing without an offer. */
export const SubscribeAndSaveOptions = ({
  choice: { offer, frequency, setFrequency },
  oneTimePrice,
}: {
  choice: SubscribeAndSaveChoice;
  oneTimePrice: number;
}) => {
  const name = useId();
  if (!offer) return null;
  const option = frequency && offerOption(offer, frequency);
  return (
    <fieldset
      className="bc-subscribe-and-save-options"
      style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '0 0 16px', padding: 0, border: 'none' }}
    >
      <legend style={{ padding: 0, marginBottom: 10, fontWeight: 600 }}>How do you want it?</legend>
      <label style={{ ...rowStyle(!frequency), ...optionStyle, flexDirection: 'row' }}>
        <input
          type="radio"
          name={name}
          style={radioStyle}
          checked={!frequency}
          onChange={() => setFrequency(undefined)}
        />
        <span style={{ flex: 1 }}>One-time purchase</span>
        <span>{formatMoney(oneTimePrice)}</span>
      </label>
      <div style={rowStyle(!!frequency)}>
        <label style={optionStyle}>
          <input
            type="radio"
            name={name}
            style={radioStyle}
            checked={!!frequency}
            onChange={() => setFrequency(defaultOfferOption(offer).frequency)}
          />
          <span style={{ flex: 1 }}>
            Subscribe & Save
            <span style={pillStyle}>{offerDiscountLabel(offer)}</span>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
            <s style={mutedStyle}>{formatCents(offer.unitPriceCents)}</s>
            <span>{formatCents(offer.subscriberUnitPriceCents)}</span>
          </span>
        </label>
        {frequency ? (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 12px', paddingLeft: 23 }}>
            <FrequencySelect label="Deliver" frequency={frequency} offer={offer} onChange={setFrequency} />
            {option ? <span style={mutedStyle}>{`Next order ${formatOrderDay(option.nextOrderDate)}`}</span> : null}
          </div>
        ) : null}
      </div>
    </fieldset>
  );
};
